import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
  Card,
  CardHeader,
  CardBody,
  CardText,
  Button,
  Input,
  Row,
  Col,
  CardLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import api from '../../services/api';
import TeamGroupingModal from './TeamGroupingModal';
import EditTeamModal from './EditTeamModal';

const LeagueTeams = ({ league, onTeamCountChange }) => {
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingLeagueTeam, setEditingLeagueTeam] = useState(null);
  const [editedTeamName, setEditedTeamName] = useState('');
  const [editedSchedule, setEditedSchedule] = useState('');
  const [editedStart, setEditedStart] = useState('');
  const [editedEnd, setEditedEnd] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newSchedule, setNewSchedule] = useState('');
  const [emailModal, setEmailModal] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ success: false, message: '' });
  const [addToScheduleModal, setAddToScheduleModal] = useState(false);
  const [addToScheduleStatus, setAddToScheduleStatus] = useState({ success: false, message: '' });
  const [addToScheduleResult, setAddToScheduleResult] = useState(null);
  const [showEmailAllConfirm, setShowEmailAllConfirm] = useState(false);
  const [showEmailScheduleConfirm, setShowEmailScheduleConfirm] = useState(false);
  const [emailScheduleTarget, setEmailScheduleTarget] = useState({ leagueId: null, teamId: null });
  const [emailScheduleTestMode, setEmailScheduleTestMode] = useState(true);
  const [emailAllTestMode, setEmailAllTestMode] = useState(true);
  const [emailingAll, setEmailingAll] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importStatus, setImportStatus] = useState({ success: false, message: '' });
  const [importing, setImporting] = useState(false);
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [groupingModal, setGroupingModal] = useState(false);
  const [groupBySchedule, setGroupBySchedule] = useState(false);

  useEffect(() => {
    const duration = Number(league?.service?.duration);
    if (!editedStart || !duration) {
      setEditedEnd('');
      return;
    }

    setEditedEnd(moment(editedStart, 'HH:mm').add(duration, 'minutes').format('HH:mm'));
  }, [editedStart, league?.service?.duration]);

  const toggleEditModal = () => setEditModal(!editModal);
  const toggleAddModal = () => {
    setAddModal(!addModal);
    if (!addModal) {
      setNewTeamName('');
      setNewSchedule('');
    }
  };
  const toggleImportModal = () => {
    setImportModal(!importModal);
    if (!importModal) {
      setImportFile(null);
      setImportStatus({ success: false, message: '' });
      setDeleteExisting(false);
    }
  };
  const toggleGroupingModal = () => setGroupingModal(!groupingModal);
  
  const fetchLeagueTeams = useCallback(async (leagueId) => {
    try {
      const response = await api.get(`/league-teams/?league=${leagueId}`);
      setLeagueTeams(response.data);
      if (onTeamCountChange) {
        onTeamCountChange(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching league teams:', error);
    }
  }, [onTeamCountChange]);

  const fetchSchedules = useCallback(async (leagueId) => {
    try {
      const response = await api.get(`/league-schedules/?league=${leagueId}`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, []);

  useEffect(() => {
    if (league?.id) {
      fetchLeagueTeams(league.id);
      fetchSchedules(league.id);
      //fetchTeams();
    }
  }, [league?.id, fetchLeagueTeams, fetchSchedules]);

  // const fetchTeams = async () => {
  //   try {
  //     const response = await api.get('/teams/');
  //     setTeams(response.data);
  //   } catch (error) {
  //     console.error('Error fetching teams:', error);
  //   }
  // };

  /*   const addLeagueTeam = async () => {
      if (!league?.id || !newLeagueTeam.team_id) return;
      try {
        await api.post('/league-teams/', {
          league: league.id,
          ...newLeagueTeam,
        });
        fetchLeagueTeams(league.id);
        setNewLeagueTeam({
          team_id: '',
          group: '',
          start: '',
          end: '',
        });
      } catch (error) {
        console.error('Error adding league team:', error);
      }
    };
   */
  const formatTime = (time) => {
    if (!time) return null;
    try {
      const [hour, minute] = time.split(':');
      const date = new Date();
      date.setHours(hour, minute);
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch {
      return time;
    }
  };

  const handleExport = async (leagueId, teamId) => {
    try {
      const response = await api.get(`/export/league-events/${leagueId}/?team=${teamId}`, {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'];
      let filename = `league_${leagueId}_events.csv`;
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/['"]/g, '');
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting events:', error);
    }
  };

  const handleExportLeaguePlayers = async (leagueId) => {
    try {
      const response = await api.get(`/export/league-players/${leagueId}/`, {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'];
      let filename = `league_${leagueId}_players.csv`;
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/['"]/g, '');
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting league players:', error);
    }
  };

  const handleExportRegistrations = async (leagueId) => {
    try {
      const response = await api.get(`/export/league-team-registrations/${leagueId}/`, {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'];
      let filename = `league_${leagueId}_team_registrations.csv`;
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/['"]/g, '');
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting league team registrations:', error);
    }
  };

  const handleEmailSchedule = async (leagueId, teamId, isTestMode = true) => {
    try {
      const payload = {
        league_id: leagueId,
        team_id: teamId,
      };

      if (isTestMode) {
        payload.mode = 'test';
      }

      await api.post('/league-events/email/', {
        ...payload,
      });
      setEmailStatus({ success: true, message: 'Schedule email sent successfully!' });
      setEmailModal(true);
    } catch (error) {
      console.error('Error sending schedule email:', error);
      const errorData = error.response?.data;
      const apiErrorMessage =
        errorData?.detail ||
        errorData?.message ||
        errorData?.error ||
        (Array.isArray(errorData?.non_field_errors) ? errorData.non_field_errors.join(' ') : null) ||
        (typeof errorData === 'string' ? errorData : null) ||
        error.message ||
        'Failed to send schedule email. Please try again.';

      setEmailStatus({ success: false, message: apiErrorMessage });
      setEmailModal(true);
    }
  };

  const openEmailScheduleConfirm = (leagueId, teamId) => {
    setEmailScheduleTarget({ leagueId, teamId });
    setEmailScheduleTestMode(true);
    setShowEmailScheduleConfirm(true);
  };

  const handleConfirmEmailSchedule = async () => {
    const { leagueId, teamId } = emailScheduleTarget;
    setShowEmailScheduleConfirm(false);

    if (!leagueId || !teamId) {
      return;
    }

    await handleEmailSchedule(leagueId, teamId, emailScheduleTestMode);
  };

  const handleAddToSchedule = async (leagueTeam) => {
    const scheduleId = leagueTeam?.schedule;
    const teamId = leagueTeam?.team;

    if (!scheduleId || !teamId) {
      setAddToScheduleResult(null);
      setAddToScheduleStatus({
        success: false,
        message: 'This team must be assigned to a schedule before it can be added.',
      });
      setAddToScheduleModal(true);
      return;
    }

    try {
      const response = await api.post('/league-schedule/add-team/', {
        schedule_id: scheduleId,
        team_id: teamId,
      });
      await fetchLeagueTeams(league.id);

      const result = response?.data || {};
      setAddToScheduleResult(result);
      setAddToScheduleStatus({
        success: true,
        message: result.message || 'Team added to schedule successfully.',
      });
      setAddToScheduleModal(true);
    } catch (error) {
      console.error('Error adding team to schedule:', error);
      const errorData = error.response?.data;
      const apiErrorMessage =
        errorData?.detail ||
        errorData?.message ||
        errorData?.error ||
        (Array.isArray(errorData?.non_field_errors) ? errorData.non_field_errors.join(' ') : null) ||
        (typeof errorData === 'string' ? errorData : null) ||
        error.message ||
        'Failed to add team to schedule. Please try again.';

      setAddToScheduleResult(null);
      setAddToScheduleStatus({ success: false, message: apiErrorMessage });
      setAddToScheduleModal(true);
    }
  };

  const handleEmailAllTeams = async () => {
    setShowEmailAllConfirm(false);
    setEmailingAll(true);

    let successCount = 0;
    let failCount = 0;

    for (const team of leagueTeams) {
      try {
        const payload = {
          league_id: team.league,
          team_id: team.team,
        };

        if (emailAllTestMode) {
          payload.mode = 'test';
        }

        await api.post('/league-events/email/', {
          ...payload,
        });
        successCount++;
      } catch (error) {
        console.error(`Error sending email to team ${team.team_display}:`, error);
        failCount++;
      }
    }

    setEmailingAll(false);

    if (failCount === 0) {
      setEmailStatus({
        success: true,
        message: `Successfully sent schedule emails to all ${successCount} teams!`
      });
    } else if (successCount === 0) {
      setEmailStatus({
        success: false,
        message: `Failed to send emails to all ${failCount} teams. Please try again.`
      });
    } else {
      setEmailStatus({
        success: true,
        message: `Sent emails to ${successCount} teams. Failed to send to ${failCount} teams.`
      });
    }
    setEmailModal(true);
  };

  const openEmailAllConfirm = () => {
    setEmailAllTestMode(true);
    setShowEmailAllConfirm(true);
  };

  const handleEditClick = async (leagueTeam, name) => {
    setEditingTeam(leagueTeam.team);
    setEditingLeagueTeam(leagueTeam);
    setEditedTeamName(name || '');
    setEditedSchedule(leagueTeam.schedule || '');
    setEditedStart((leagueTeam.start || '').slice(0, 5));
    
    // Fetch the full team data to get notes
    try {
      const response = await api.get(`/teams/${leagueTeam.team}/`);
      setEditedNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching team details:', error);
      setEditedNotes('');
    }
    
    setEditModal(true);
  };

  const handleSaveTeamName = async () => {
    if (!editingTeam) return;
    try {
      await api.put(`/teams/${editingTeam}/`, { name: editedTeamName, notes: editedNotes });
      await api.put(`/league-teams/${editingLeagueTeam.id}/`, {
        league: league.id,
        team: editingTeam,
        schedule: editedSchedule || null,
        start: editedStart ? `${editedStart}:00` : null,
        end: editedEnd ? `${editedEnd}:00` : null
      });
      setEditModal(false);
      setEditingTeam(null);
      setEditedTeamName('');
      setEditedSchedule('');
      setEditedStart('');
      setEditedEnd('');
      setEditedNotes('');
      fetchLeagueTeams(league.id);
    } catch (error) {
      console.error('Error updating team name:', error);
    }
  };

  const handleAddTeam = async () => {
    if (!newTeamName || !league?.id) return;
    
    // Check for minimum 3 characters
    const trimmedName = newTeamName.trim();
    if (trimmedName.length < 3) {
      alert('Team name must be at least 3 characters long.');
      return;
    }
    
    // Check for duplicate team name using API
    try {
      const checkResponse = await api.get(
        `/leagues/${league.guid}/check-team-name/?name=${encodeURIComponent(trimmedName)}`
      );
      
      if (!checkResponse.data.available) {
        alert(`A team with the name "${trimmedName}" already exists in this league. Please choose a different name.`);
        return;
      }
      
      // First create the team
      const teamResponse = await api.post('/teams/', { 
        name: trimmedName,
        league: league.id
      });
      const newTeamId = teamResponse.data.id;
      
      // Then add the team to the league
      await api.post('/league-teams/', {
        league: league.id,
        team: newTeamId,
        schedule: newSchedule || null
      });
      
      setAddModal(false);
      setNewTeamName('');
      setNewSchedule('');
      fetchLeagueTeams(league.id);
    } catch (error) {
      console.error('Error adding team:', error);
      alert('Failed to add team. Please try again.');
    }
  };

  const handleDeleteLeagueTeam = async () => {
    if (!editingTeam) return;
    try {
      // First remove the team from the league
      await api.delete(`/league-teams/${editingLeagueTeam.id}/`);
      // Then delete the team itself
      await api.delete(`/teams/${editingTeam}/`);
      setShowDeleteConfirm(false);
      setEditModal(false);
      setEditingTeam(null);
      fetchLeagueTeams(league.id);
    } catch (error) {
      console.error('Error deleting league team:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setImportStatus({ success: false, message: 'Please select a valid CSV file.' });
        setImportFile(null);
        return;
      }
      setImportFile(file);
      setImportStatus({ success: false, message: '' });
    }
  };

  const handleImportTeams = async () => {
    if (!importFile || !league?.id) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const url = `/import/league-teams/${league.id}/${deleteExisting ? '?delete_existing=true' : ''}`;
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setImportStatus({ 
        success: true, 
        message: response.data.message || 'Teams imported successfully!' 
      });
      fetchLeagueTeams(league.id);
      setImportFile(null);
    } catch (error) {
      console.error('Error importing teams:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to import teams. Please check your CSV format and try again.';
      setImportStatus({ success: false, message: errorMessage });
    } finally {
      setImporting(false);
    }
  };

  if (!league?.id) {
    return <p className="text-muted small">Select a league to manage its teams.</p>;
  }

  const getScheduleTitle = (scheduleId) => {
    if (!scheduleId) return 'No Schedule';
    return schedules.find((s) => String(s.id) === String(scheduleId))?.title || String(scheduleId);
  };

  const scheduledTeamGroups = schedules
    .map((schedule) => ({
      key: String(schedule.id),
      title: schedule.title,
      teams: leagueTeams.filter((lt) => String(lt.schedule) === String(schedule.id)),
    }))
    .filter((group) => group.teams.length > 0);

  const teamsWithoutSchedule = leagueTeams.filter((lt) => !lt.schedule);
  const groupedTeams = teamsWithoutSchedule.length > 0
    ? [
        ...scheduledTeamGroups,
        { key: 'no-schedule', title: 'No Schedule', teams: teamsWithoutSchedule },
      ]
    : scheduledTeamGroups;

  const renderTeamCard = (lt) => {
    const players = lt.players || [];
    const hasSchedule = lt.has_schedule !== false;

    return (
      <Col key={lt.id} xs="6" sm="4" md="3" lg="2">
        <Card className="h-100 border-0 shadow-sm p-2">
          <CardHeader className="bg-light fw-semibold small py-2 px-2 d-flex align-items-center justify-content-between">
            <span className="text-truncate">{lt.team_display || 'Unnamed Team'}</span>
            <Button
              color="link"
              className="text-secondary p-0 ms-1 flex-shrink-0"
              style={{ lineHeight: 1 }}
              title="Edit team"
              onClick={() => handleEditClick(lt, lt.team_display)}
            >
              <i className="bi bi-pencil-fill" />
            </Button>
          </CardHeader>
          <CardBody className="small py-2 px-2 d-flex flex-column justify-content-between">
            {players.length > 0 && (
              <>
                {players.map((p) => (
                  <CardText key={p.id} className="mb-0 text-truncate">
                    {p.first_name} {p.last_name}
                  </CardText>
                ))}
                <hr className="my-2" />
              </>
            )}
            {lt.group && (
              <CardText className="mb-1">
                <span className="text-muted">Group:</span> {lt.group}
              </CardText>
            )}
            {lt.schedule && (
              <CardText className="mb-1">
                {getScheduleTitle(lt.schedule)}
              </CardText>
            )}
            {lt.start && (
              <CardText className="mb-1">{formatTime(lt.start)}</CardText>
            )}
            <div className="mt-auto pt-2 mb-0">
              {hasSchedule ? (
                <>
                  <div>
                    <CardLink
                      onClick={() => handleExport(lt.league, lt.team)}
                      className="small"
                      style={{ cursor: 'pointer' }}
                    >
                      Export events
                    </CardLink>
                  </div>
                  <div>
                    <CardLink
                      onClick={() => openEmailScheduleConfirm(lt.league, lt.team)}
                      className="small"
                      style={{ cursor: 'pointer' }}
                    >
                      Email schedule
                    </CardLink>
                  </div>
                </>
              ) : (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => handleAddToSchedule(lt)}
                >
                  Add to Schedule
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <Card>
        <CardBody className="pt-3 bg-dark bg-opacity-10">
          {leagueTeams.length === 0 ? (
            <p className="text-muted small">No teams assigned to this league.</p>
          ) : (
            groupBySchedule ? (
              <div>
                {groupedTeams.map((group) => (
                  <div key={group.key} className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <span className={`badge rounded-pill me-2 ${group.key === 'no-schedule' ? 'bg-secondary' : 'bg-primary'}`}>
                        {group.title}
                      </span>
                      <span className="badge rounded-pill bg-light text-dark border">
                        {group.teams.length} Team{group.teams.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <Row className="g-2">
                      {group.teams.map(renderTeamCard)}
                    </Row>
                  </div>
                ))}
              </div>
            ) : (
              <Row className="g-2">
                {leagueTeams.map(renderTeamCard)}
              </Row>
            )
          )}

          {/* Action Buttons */}
          {leagueTeams.length > 0 && (
            <div className="mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Button
                  color="success"
                  size="sm"
                  onClick={toggleAddModal}
                >
                  + Add Team
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  onClick={toggleImportModal}
                >
                  Import Teams
                </Button>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleExportLeaguePlayers(league.id)}
                >
                  Export Players
                </Button>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleExportRegistrations(league.id)}
                >
                  Export Registrations
                </Button>
                <Button
                  color="light"
                  size="sm"
                  onClick={toggleGroupingModal}
                >
                  Manage Team Groups
                </Button>
                <div className="form-check ms-2 mb-0 d-flex align-items-center">
                  <Input
                    type="checkbox"
                    id="groupByScheduleCheckbox"
                    checked={groupBySchedule}
                    onChange={(e) => setGroupBySchedule(e.target.checked)}
                    className="form-check-input mt-0"
                  />
                  <label className="form-check-label ms-2" htmlFor="groupByScheduleCheckbox">
                    Group By Schedule
                  </label>
                </div>
              </div>
              <Button
                color="primary"
                size="sm"
                onClick={openEmailAllConfirm}
                disabled={emailingAll}
              >
                {emailingAll ? 'Sending Emails...' : 'Email All Teams'}
              </Button>
            </div>
          )}
          {leagueTeams.length === 0 && (
            <div className="mt-3">
              <Button
                color="success"
                size="sm"
                onClick={toggleAddModal}
                className="me-2"
              >
                + Add Team
              </Button>
              <Button
                color="warning"
                size="sm"
                onClick={toggleImportModal}
                className="me-2"
              >
                Import Teams
              </Button>
              <Button
                color="info"
                size="sm"
                onClick={() => handleExportLeaguePlayers(league.id)}
              >
                Export Players
              </Button>
              <Button
                color="info"
                size="sm"
                onClick={() => handleExportRegistrations(league.id)}
                className="ms-2"
              >
                Export Registrations
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Edit Team Name Modal */}
      <EditTeamModal
        isOpen={editModal}
        toggle={toggleEditModal}
        editedTeamName={editedTeamName}
        editedSchedule={editedSchedule}
        editedStart={editedStart}
        editedEnd={editedEnd}
        editedNotes={editedNotes}
        schedules={schedules}
        editingTeam={editingTeam}
        leagueId={league.guid}
        onTeamNameChange={setEditedTeamName}
        onScheduleChange={setEditedSchedule}
        onStartChange={setEditedStart}
        onNotesChange={setEditedNotes}
        onSave={handleSaveTeamName}
        onDeleteClick={() => setShowDeleteConfirm(true)}
      />

      {/* Add Team Modal */}
      <Modal isOpen={addModal} toggle={toggleAddModal}>
        <ModalHeader toggle={toggleAddModal}>
          Add New Team
        </ModalHeader>
        <ModalBody>
          <Row className="mb-3">
            <Col xs="12">
              <label className="form-label">Team Name</label>
              <Input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter team name (min. 3 characters)"
              />
              {newTeamName && newTeamName.trim().length < 3 && (
                <small className="text-danger">Team name must be at least 3 characters long.</small>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs="12">
              <label className="form-label">Assigned Schedule (Optional)</label>
              <Input
                type="select"
                value={newSchedule}
                onChange={(e) => setNewSchedule(e.target.value)}
              >
                <option value="">-- No Schedule --</option>
                {schedules.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.title}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddTeam} disabled={!newTeamName || newTeamName.trim().length < 3}>
            Add Team
          </Button>{' '}
          <Button color="secondary" onClick={toggleAddModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} toggle={() => setShowDeleteConfirm(false)}>
        <ModalHeader toggle={() => setShowDeleteConfirm(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to permanently remove this team from the league?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteLeagueTeam}>
            Yes, Delete
          </Button>{' '}
          <Button color="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Email All Confirmation Modal */}
      <Modal isOpen={showEmailAllConfirm} toggle={() => setShowEmailAllConfirm(false)}>
        <ModalHeader toggle={() => setShowEmailAllConfirm(false)}>
          Confirm Email All Teams
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to send schedule emails to all {leagueTeams.length} teams in this league?</p>
          <div className="form-check">
            <Input
              type="checkbox"
              id="emailAllTestMode"
              checked={emailAllTestMode}
              onChange={(e) => setEmailAllTestMode(e.target.checked)}
              className="form-check-input"
            />
            <label className="form-check-label" htmlFor="emailAllTestMode">
              Test Mode (only emails the league admins)
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleEmailAllTeams}>
            Yes, Send Emails
          </Button>{' '}
          <Button color="secondary" onClick={() => setShowEmailAllConfirm(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Email Schedule Confirmation Modal */}
      <Modal isOpen={showEmailScheduleConfirm} toggle={() => setShowEmailScheduleConfirm(false)}>
        <ModalHeader toggle={() => setShowEmailScheduleConfirm(false)}>
          Confirm Schedule Email
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to send this team schedule email?</p>
          <div className="form-check">
            <Input
              type="checkbox"
              id="emailScheduleTestMode"
              checked={emailScheduleTestMode}
              onChange={(e) => setEmailScheduleTestMode(e.target.checked)}
              className="form-check-input"
            />
            <label className="form-check-label" htmlFor="emailScheduleTestMode">
              Test Mode (only emails the league admins)
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleConfirmEmailSchedule}>
            Yes, Send Email
          </Button>{' '}
          <Button color="secondary" onClick={() => setShowEmailScheduleConfirm(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Email Status Modal */}
      <Modal isOpen={emailModal} toggle={() => setEmailModal(false)}>
        <ModalHeader toggle={() => setEmailModal(false)}>
          {emailStatus.success ? 'Success' : 'Error'}
        </ModalHeader>
        <ModalBody>
          {emailStatus.message}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEmailModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add To Schedule Status Modal */}
      <Modal isOpen={addToScheduleModal} toggle={() => setAddToScheduleModal(false)}>
        <ModalHeader toggle={() => setAddToScheduleModal(false)}>
          {addToScheduleStatus.success ? 'Success' : 'Error'}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">{addToScheduleStatus.message}</p>
          {addToScheduleStatus.success && addToScheduleResult && (
            <div className="small text-muted">
              Weeks Scheduled: {addToScheduleResult.weeks_scheduled}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAddToScheduleModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Import Teams Modal */}
      <Modal isOpen={importModal} toggle={toggleImportModal}>
        <ModalHeader toggle={toggleImportModal}>
          Import Teams from CSV
        </ModalHeader>
        <ModalBody>
          {importStatus.message && (
            <div className={`alert ${importStatus.success ? 'alert-success' : 'alert-danger'}`}>
              {importStatus.message}
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Select CSV File</label>
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={importing}
            />
            <small className="text-muted">
              Please upload a CSV file with team information.
            </small>
          </div>
          <div className="mb-3">
            <div className="form-check">
              <Input
                type="checkbox"
                id="deleteExistingCheckbox"
                checked={deleteExisting}
                onChange={(e) => setDeleteExisting(e.target.checked)}
                disabled={importing}
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor="deleteExistingCheckbox">
                Delete existing teams before importing
              </label>
            </div>
            {deleteExisting && (
              <small className="text-warning d-block mt-1">
                Warning: This will remove all current teams from this league.
              </small>
            )}
          </div>
          {importFile && (
            <div className="alert alert-info">
              <strong>Selected file:</strong> {importFile.name}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button 
            color="success" 
            onClick={handleImportTeams}
            disabled={!importFile || importing}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>{' '}
          <Button color="secondary" onClick={toggleImportModal} disabled={importing}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Team Grouping Modal */}
      <TeamGroupingModal
        isOpen={groupingModal}
        toggle={toggleGroupingModal}
        league={league}
        onGroupsUpdated={() => fetchLeagueTeams(league.id)}
      />
    </>
  );
};

export default LeagueTeams;
